import React, { useEffect, useId, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { hasNotificationViewport, notify, splitMessage } from "../services/notify";
import type { NotificationKind } from "../services/notify";
import { rawColors } from "../design-system/tokens/colors";

type ToastPosition =
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right"
  | "top-center"
  | "bottom-center";

const positionStyles = {
  "top-left": css`top: 24px; left: 24px;`,
  "top-right": css`top: 24px; right: 24px;`,
  "bottom-left": css`bottom: 24px; left: 24px;`,
  "bottom-right": css`bottom: 24px; right: 24px;`,
  "top-center": css`top: 24px; left: 50%; transform: translateX(-50%);`,
  "bottom-center": css`bottom: 24px; left: 50%; transform: translateX(-50%);`,
};

const ToastWrapper = styled.div<{ position: ToastPosition; $visible: boolean }>`
  position: fixed;
  z-index: 2000;
  min-width: 220px;
  max-width: 340px;
  padding: 14px 24px;
  background: #222;
  color: ${rawColors.text.white};
  border-radius: 8px;
  font-size: 15px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.12);
  opacity: ${({ $visible }) => ($visible ? 0.96 : 0)};
  transform: ${({ $visible }) => ($visible ? "translateY(0)" : "translateY(20px)")};
  pointer-events: none;
  transition: opacity 0.4s cubic-bezier(.4,0,.2,1), transform 0.4s cubic-bezier(.4,0,.2,1);
  ${({ position }) => positionStyles[position]};
`;

interface ToastProps {
  message: string;
  position?: ToastPosition;
  duration?: number; // ms
  onClose?: () => void;
  /** How the app-wide stack shows it; defaults to info. */
  kind?: NotificationKind;
}

/**
 * A one-off message. While the app-wide stack is mounted (organisms/ToastViewport, in App) the
 * message joins it as a notification (split into a title and a body) and `onClose` runs when it
 * leaves; without the stack (isolated tests, stories) it renders here as before.
 * New code calls `notify()` from services/notify instead.
 */
const Toast: React.FC<ToastProps> = ({
  message,
  position = "bottom-left",
  duration = 2500,
  onClose,
  kind = "info",
}) => {
  const [visible, setVisible] = useState(false);
  const forwarded = hasNotificationViewport();
  const legacyId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!forwarded || !message) return undefined;
    let active = true;
    const handle = notify({
      id: `legacy-toast-${legacyId}`,
      kind,
      ...splitMessage(message),
      duration,
      onDismiss: () => {
        if (active) onCloseRef.current?.();
      },
    });
    return () => {
      // Replaced or unmounted by its caller: leave quietly, without calling onClose.
      active = false;
      handle.dismiss();
    };
  }, [forwarded, message, duration, kind, legacyId]);

  useEffect(() => {
    if (forwarded) return undefined;
    setVisible(true);
    const hideTimer = setTimeout(() => setVisible(false), duration);
    const closeTimer = setTimeout(() => onClose?.(), duration + 400); // match transition
    return () => {
      clearTimeout(hideTimer);
      clearTimeout(closeTimer);
    };
  }, [forwarded, duration, onClose, message]);

  if (!message || forwarded) return null;
  return <ToastWrapper position={position} $visible={visible}>{message}</ToastWrapper>;
};

export default Toast;
